import { StreamId } from "@common/domain";

export interface RemoteConferenceStreamLike {
  readonly streamId: StreamId;
  subscribeStream(): Promise<unknown>;
  destroy(): Promise<void>;
}

export interface RemoteConferenceStreamManagerOptions<
  T extends RemoteConferenceStreamLike,
  R
> {
  create(request: R): T;
  // Called only after a successful subscribe, so a tile never exists for a
  // stream that is not (yet) subscribed.
  onAdd(stream: T): void;
  onRemove(stream: T): void;
  onError(
    error: unknown,
    context: { streamId: StreamId; attempt: number; willRetry: boolean }
  ): void;
  // Delay before each retry; its length bounds the number of retries.
  retryDelaysMs?: readonly number[];
  // A subscribe error that a retry of the same request cannot fix.
  isPermanentError?(error: unknown): boolean;
  // A monotonic clock, see reconcile().
  now?(): number;
}

interface Entry<T, R> {
  // Bumped on every add/delete/dispose of the stream id: a running attempt or
  // a pending retry that sees a newer version is stale and is cancelled.
  version: number;
  request: R | null;
  current: T | null;
  currentVersion: number;
  running: Promise<void> | null;
  wake: (() => void) | null;
}

/**
 * Converges the subscribed remote streams to the latest requested state,
 * one stream id at a time:
 * - at most one subscription attempt in flight per stream id;
 * - a failed subscribe is retried with a bounded backoff;
 * - a delete/dispose (or a newer add) cancels a pending retry and disposes a
 *   subscription that completes after it was cancelled (no ghost stream);
 * - reconcile() converges to an authoritative stream list, for a client that
 *   missed room stream events.
 */
export class RemoteConferenceStreamManager<
  T extends RemoteConferenceStreamLike,
  R
> {
  static readonly RETRY_DELAYS_MS: readonly number[] = [1_000, 2_000, 4_000];

  private readonly _entries = new Map<StreamId, Entry<T, R>>();
  private readonly _retryDelaysMs: readonly number[];
  private readonly _now: () => number;
  // When each stream id was last added/deleted, and when the manager was last
  // disposed: an authoritative list requested before that is stale for it.
  private readonly _changedAt = new Map<StreamId, number>();
  private _disposedAt = -Infinity;

  constructor(
    private readonly _options: RemoteConferenceStreamManagerOptions<T, R>
  ) {
    this._retryDelaysMs =
      _options.retryDelaysMs ?? RemoteConferenceStreamManager.RETRY_DELAYS_MS;
    this._now = _options.now ?? (() => performance.now());
  }

  get streams(): T[] {
    return [...this._entries.values()].flatMap((_) =>
      _.current ? [_.current] : []
    );
  }

  // (Re)subscribe the stream. An already subscribed stream with the same id is
  // replaced, e.g. after the publisher restarted it.
  add(id: StreamId, request: R): Promise<void> {
    this._changedAt.set(id, this._now());
    return this.update(id, request);
  }

  delete(id: StreamId): Promise<void> {
    this._changedAt.set(id, this._now());
    return this._entries.has(id) ? this.update(id, null) : Promise.resolve();
  }

  async dispose(): Promise<void> {
    this._disposedAt = this._now();
    const deletes = [...this._entries.keys()].map((_) => this.delete(_));
    this._changedAt.clear();

    await Promise.all(deletes);
  }

  /**
   * Converges to `requests`, the authoritative stream list requested at
   * `requestedAt` (same clock as `now`):
   * - a listed stream that is neither subscribed nor being subscribed is
   *   added (missed add event, or retries exhausted);
   * - a known stream that is not listed is deleted (missed delete event);
   * - a subscribed or in-flight stream is left alone (no resubscribe);
   * - a stream added/deleted since `requestedAt` is left alone, and the whole
   *   list is ignored if the manager was disposed since: the list is older.
   */
  reconcile(
    requests: ReadonlyMap<StreamId, R>,
    requestedAt: number
  ): Promise<void> {
    if (requestedAt <= this._disposedAt) {
      return Promise.resolve();
    }

    const isFresh = (id: StreamId) =>
      (this._changedAt.get(id) ?? -Infinity) < requestedAt;
    const updates: Promise<void>[] = [];

    for (const [id, entry] of this._entries) {
      if (entry.request && !requests.has(id) && isFresh(id)) {
        updates.push(this.delete(id));
      }
    }

    for (const [id, request] of requests) {
      const entry = this._entries.get(id);
      const isActive = !!entry?.request && !!(entry.current || entry.running);
      if (!isActive && isFresh(id)) {
        updates.push(this.add(id, request));
      }
    }

    return Promise.all(updates).then(() => undefined);
  }

  private update(id: StreamId, request: R | null): Promise<void> {
    let entry = this._entries.get(id);
    if (!entry) {
      entry = {
        version: 0,
        request: null,
        current: null,
        currentVersion: -1,
        running: null,
        wake: null
      };
      this._entries.set(id, entry);
    }

    entry.version++;
    entry.request = request;
    entry.wake?.();

    return entry.running ?? this.start(id, entry);
  }

  private start(id: StreamId, entry: Entry<T, R>): Promise<void> {
    const running = this.run(id, entry).finally(() => {
      entry.running = null;
      if (!entry.request && !entry.current && this._entries.get(id) === entry) {
        this._entries.delete(id);
      }
    });
    entry.running = running;

    return running;
  }

  private async run(id: StreamId, entry: Entry<T, R>): Promise<void> {
    let attempt = 0;
    let attemptVersion = entry.version;

    for (;;) {
      const version = entry.version;
      const request = entry.request;

      if (version !== attemptVersion) {
        attempt = 0;
        attemptVersion = version;
      }

      if (entry.current && entry.currentVersion !== version) {
        await this.removeCurrent(id, entry);
        continue;
      }

      if (!request || entry.current) {
        return;
      }

      attempt++;
      if (!(await this.subscribe(id, entry, request, version, attempt))) {
        return;
      }
    }
  }

  private async removeCurrent(id: StreamId, entry: Entry<T, R>) {
    const current = entry.current;
    if (!current) {
      return;
    }

    entry.current = null;
    this._options.onRemove(current);
    await this.destroy(current, id);
  }

  // One subscribe attempt for `version`. Resolves false when the retries are
  // exhausted, true when the run loop must re-evaluate the entry.
  private async subscribe(
    id: StreamId,
    entry: Entry<T, R>,
    request: R,
    version: number,
    attempt: number
  ): Promise<boolean> {
    let rcs: T | null = null;

    try {
      rcs = this._options.create(request);
      await rcs.subscribeStream();
    } catch (error) {
      if (rcs) {
        await this.destroy(rcs, id);
      }

      return this.retryAfterFailure(id, entry, version, attempt, error);
    }

    if (entry.version !== version) {
      // Cancelled while subscribing.
      await this.destroy(rcs, id);
      return true;
    }

    entry.current = rcs;
    entry.currentVersion = version;
    this._options.onAdd(rcs);

    return true;
  }

  private async retryAfterFailure(
    id: StreamId,
    entry: Entry<T, R>,
    version: number,
    attempt: number,
    error: unknown
  ): Promise<boolean> {
    if (entry.version !== version) {
      return true;
    }

    const delay = this._retryDelaysMs[attempt - 1];
    const willRetry =
      delay !== undefined && !this._options.isPermanentError?.(error);
    this._options.onError(error, { streamId: id, attempt, willRetry });

    if (!willRetry) {
      return false;
    }

    await this.sleep(entry, delay);
    return true;
  }

  // Resolves after the delay, or early when the entry is updated.
  private sleep(entry: Entry<T, R>, delayMs: number) {
    return new Promise<void>((resolve) => {
      const timer = setTimeout(done, delayMs);

      function done() {
        clearTimeout(timer);
        entry.wake = null;
        resolve();
      }

      entry.wake = done;
    });
  }

  private async destroy(stream: T, id: StreamId) {
    try {
      await stream.destroy();
    } catch (error) {
      this._options.onError(error, {
        streamId: id,
        attempt: 0,
        willRetry: false
      });
    }
  }
}
